import { JSX, useMemo } from 'react';

import { twMerge } from 'tailwind-merge';

import ArrowRightIcon from '../../assets/ArrowRightIcon';
import { CfAsset, SurfaceLevel } from '../../types';
import Link from '../atoms/Link';
import CfImage from '../CfImage';
import Icon from '../Icon/Icon';
import AnimatedArrow from './AnimatedArrow';

interface InternalIcon {
  iconFile: CfAsset;
  icon?: never;
  iconAsset?: never;
}

interface CfIcon {
  iconAsset: CfAsset;
  icon?: never;
  iconFile?: never;
}

interface LocalIcon {
  icon: JSX.Element;
  iconAsset?: never;
  iconFile?: never;
}

export type IconButtonProps = {
  animatedArrowClassName?: string;
  className?: string;
  disabled?: boolean;
  href?: string;
  inActive?: boolean;
  newTab?: boolean;
  noSurface?: boolean;
  onClick?: () => void;
  type?: 'button' | 'reset' | 'submit';
} & (CfIcon | InternalIcon | LocalIcon);

const getDisplayIcon = ({
  icon,
  iconAsset,
  iconFile,
}: Pick<IconButtonProps, 'icon' | 'iconAsset' | 'iconFile' | 'noSurface'>) => {
  if (iconFile) {
    return <Icon assetFile={iconFile} surfaceLevel={SurfaceLevel.L2} />;
  }

  if (iconAsset) {
    return <CfImage {...iconAsset} />;
  }

  return icon;
};

const IconButton = ({
  className: originalClassName,
  disabled,
  href,
  icon,
  iconAsset,
  iconFile,
  newTab,
  noSurface: originalNoSurface,
  onClick,
  type = 'button',
}: IconButtonProps) => {
  const displayIcon = useMemo(() => getDisplayIcon({
    icon,
    iconAsset,
    iconFile,
  }), [
    icon,
    iconAsset,
    iconFile,
  ]);

  const noSurface = originalNoSurface || !!iconFile;

  const sharedProps = {
    children: displayIcon,
    className: twMerge(
      'box-content rounded-sm block group',
      noSurface ? 'w-12 h-12' : 'w-4 h-4 p-sm',
      !href && 'cursor-default',

      // Disable state
      disabled && 'text-icon-inactive fill-icon-inactive cursor-not-allowed pointer-events-none',
      !noSurface && disabled && 'bg-surface-disabled',

      // Not disabled
      !disabled && 'text-icon-primary fill-icon-primary ',
      !noSurface && !disabled && 'bg-surface-l1',
      originalClassName
    ),
    onClick,
  };

  if (href) {
    return (
      <Link
        {...sharedProps}
        disableDefaultStyle
        href={href}
        newTab={newTab}
      />
    );
  }

  if (!onClick && type !== 'submit' && type !== 'reset') {
    return (
      <span
        {...sharedProps}
      />
    );
  }

  return (
    <button
      {...sharedProps}
      disabled={disabled}
      type={type}
    />
  );
};

export const LinkIconButton = (props: Omit<IconButtonProps, 'iconAsset' | 'iconFile'>) => {
  return (
    <IconButton
      {...props}
      icon={
        <AnimatedArrow><ArrowRightIcon /></AnimatedArrow>
      }
    />
  );
};

export default IconButton;
